import { calculateReadiness } from "../lib/readiness";
import { analyzeRisk } from "../lib/risk";
import type { ContractorProfile, JobPost, JobScopeItem, RiskFlag, ScopeCategory } from "../types";

const scopeOrder: ScopeCategory[] = ["SIZE", "ACTIVITY", "LOCATION", "TIME", "EQUIPMENT"];

type JobSeed = Omit<JobPost, "createdAt" | "updatedAt" | "riskFlags" | "readiness" | "status"> & {
  daysAgo: number;
};

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

function scope(
  idBase: string,
  values: Record<ScopeCategory, string>,
  verified: ScopeCategory[] = scopeOrder,
): JobScopeItem[] {
  return scopeOrder.map((category) => ({
    id: `${idBase}_${category.toLowerCase()}`,
    category,
    prompt: category,
    description: values[category] ?? "Not specified.",
    isVerified: verified.includes(category),
  }));
}

function job(input: JobSeed): JobPost {
  const { daysAgo, ...rest } = input;
  const updatedAt = isoDaysAgo(daysAgo);
  const createdAt = isoDaysAgo(daysAgo + 1);
  const riskText = [
    input.title,
    input.summary,
    input.scopeItems.map((item) => item.description).join(" "),
  ].join(" ");
  const riskFlags: RiskFlag[] = analyzeRisk(riskText, input.category);
  const readiness = calculateReadiness(input.scopeItems, riskFlags);
  return {
    ...rest,
    createdAt,
    updatedAt,
    riskFlags,
    readiness,
    status: "OPEN",
  };
}

export function buildDemoJobs(): JobPost[] {
  return [
    job({
      id: "job_water_valve_cap",
      title: "Emergency main water valve shutoff and pipe cap",
      summary:
        "Broken half-inch copper line in a basement utility room. Water is controlled but the section needs capping.",
      category: "PLUMBING",
      neighborhood: "Little City",
      city: "Bangor",
      state: "ME",
      budgetMin: 150,
      budgetMax: 325,
      desiredWindow: "Today within 2 hours",
      urgency: "EMERGENCY",
      daysAgo: 1,
      scopeItems: scope("water", {
        SIZE: "One broken half-inch copper pipe section under the basement stairs.",
        ACTIVITY: "Cap the damaged section and verify the main water shutoff holds.",
        LOCATION: "Basement utility room with poor lighting and tight access.",
        TIME: "Emergency response requested today within 2 hours.",
        EQUIPMENT:
          "Homeowner has towels. Contractor needs pipe cutter, caps, light, and basic plumbing kit.",
      }),
      contact: {
        primary: "homeowner@example.com",
        alternate: "207-555-0188",
        preferredMethod: "EMAIL",
      },
      materialNotes: "Contractor should bring common half-inch copper cap options.",
    }),
    job({
      id: "job_gutter_ranch",
      title: "Gutter clearing on single-story ranch",
      summary:
        "Remove leaves from front and rear gutters and flush two downspouts before rain next week.",
      category: "LANDSCAPE",
      neighborhood: "Fairmount",
      city: "Bangor",
      state: "ME",
      budgetMin: 90,
      budgetMax: 160,
      desiredWindow: "Before next Tuesday",
      urgency: "SOON",
      daysAgo: 2,
      scopeItems: scope(
        "gutter",
        {
          SIZE: "About 60 linear feet of gutter on a single-story ranch.",
          ACTIVITY: "Remove autumn leaves and flush two downspouts.",
          LOCATION: "Flat terrain on three sides. Rear corner has soft ground after rain.",
          TIME: "Complete before next Tuesday.",
          EQUIPMENT:
            "Contractor needs ladder and debris bags. Hose access still needs confirmation.",
        },
        ["SIZE", "ACTIVITY", "LOCATION", "TIME"],
      ),
      contact: {
        primary: "207-555-0122",
        alternate: "homeowner@example.com",
        preferredMethod: "SMS",
      },
    }),
    job({
      id: "job_tv_mount",
      title: "Mount 55 inch TV on living room wall",
      summary:
        "Renter needs a TV mounted using landlord-approved hardware and wants no cable concealment.",
      category: "HANDYMAN",
      neighborhood: "West End",
      city: "Portland",
      state: "ME",
      budgetMin: 80,
      budgetMax: 140,
      desiredWindow: "Saturday afternoon",
      urgency: "ROUTINE",
      daysAgo: 3,
      scopeItems: scope("tv", {
        SIZE: "One 55 inch television and one fixed wall mount.",
        ACTIVITY: "Mount TV level on drywall over studs. No in-wall wire work.",
        LOCATION: "First-floor apartment living room with elevator access.",
        TIME: "Saturday between 1300 and 1700.",
        EQUIPMENT:
          "Customer has mount and screws. Contractor should bring stud finder, level, drill, and anchors.",
      }),
      contact: { primary: "renter@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_appliance_move",
      title: "Move washer and dryer to garage",
      summary:
        "Two appliances need to move from basement to garage. Stairs are narrow and require a second person.",
      category: "MOVING",
      neighborhood: "Broadway",
      city: "Bangor",
      state: "ME",
      budgetMin: 180,
      budgetMax: 300,
      desiredWindow: "Any weekday after 1700",
      urgency: "SOON",
      daysAgo: 4,
      scopeItems: scope("appliance", {
        SIZE: "One washer and one dryer, both full size.",
        ACTIVITY: "Move both appliances from basement laundry area to detached garage.",
        LOCATION: "Narrow basement stairs, two exterior steps, 45 foot path to garage.",
        TIME: "Any weekday after 1700 this week.",
        EQUIPMENT: "Contractor needs appliance dolly, straps, and second person.",
      }),
      contact: {
        primary: "207-555-0145",
        alternate: "customer@example.com",
        preferredMethod: "SMS",
      },
    }),
    job({
      id: "job_drywall_patch",
      title: "Patch two small drywall holes",
      summary: "Two doorknob holes in hallway need patch, sand, and paint-ready finish.",
      category: "DRYWALL_PAINT",
      neighborhood: "Deering Center",
      city: "Portland",
      state: "ME",
      budgetMin: 120,
      budgetMax: 220,
      desiredWindow: "Within 10 days",
      urgency: "ROUTINE",
      daysAgo: 5,
      scopeItems: scope("drywall", {
        SIZE: "Two holes, each smaller than six inches, in hallway drywall.",
        ACTIVITY: "Patch, sand, and leave paint-ready. No paint matching required.",
        LOCATION: "First-floor hallway with clear access and parking in driveway.",
        TIME: "Flexible within 10 days.",
        EQUIPMENT:
          "Contractor supplies patch material, compound, tape, sanding block, and drop cloth.",
      }),
      contact: { primary: "homeowner2@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_furniture_assembly",
      title: "Assemble two flat-pack bookcases",
      summary:
        "Customer has two boxed bookcases in an apartment and needs assembly plus anti-tip anchoring.",
      category: "ASSEMBLY",
      neighborhood: "Munjoy Hill",
      city: "Portland",
      state: "ME",
      budgetMin: 75,
      budgetMax: 130,
      desiredWindow: "Sunday morning",
      urgency: "ROUTINE",
      daysAgo: 6,
      scopeItems: scope("bookcase", {
        SIZE: "Two flat-pack bookcases, each 72 inches tall.",
        ACTIVITY: "Assemble both units and attach anti-tip anchors to wall studs.",
        LOCATION: "Third-floor walk-up apartment. Boxes are already inside.",
        TIME: "Sunday between 0900 and 1200.",
        EQUIPMENT:
          "Customer has hardware. Contractor should bring drill, screwdriver bits, stud finder, and level.",
      }),
      contact: { primary: "207-555-0111", preferredMethod: "SMS" },
    }),
    job({
      id: "job_storm_door",
      title: "Adjust storm door latch and closer",
      summary: "Storm door does not latch cleanly and closes too fast in wind.",
      category: "HANDYMAN",
      neighborhood: "Tree Streets",
      city: "Bangor",
      state: "ME",
      budgetMin: 65,
      budgetMax: 125,
      desiredWindow: "This week",
      urgency: "ROUTINE",
      daysAgo: 7,
      scopeItems: scope("door", {
        SIZE: "One front storm door with existing latch and closer.",
        ACTIVITY: "Adjust latch alignment and closer tension. Replace small screws if stripped.",
        LOCATION: "Front entry, exterior, clear access.",
        TIME: "Any afternoon this week.",
        EQUIPMENT: "Contractor needs screwdrivers, drill, replacement screws, and small shims.",
      }),
      contact: { primary: "resident@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_yard_cleanup",
      title: "Small yard cleanup and branch haul-off",
      summary: "Light storm debris cleanup from a small backyard. No tree cutting requested.",
      category: "LANDSCAPE",
      neighborhood: "Cape Elizabeth",
      city: "Cape Elizabeth",
      state: "ME",
      budgetMin: 150,
      budgetMax: 260,
      desiredWindow: "Before Friday",
      urgency: "SOON",
      daysAgo: 8,
      scopeItems: scope("yard", {
        SIZE: "One small backyard, about 40 by 60 feet, with scattered branches.",
        ACTIVITY: "Gather branches, bag loose debris, and haul away one small trailer load.",
        LOCATION: "Backyard has side gate access. No pets on site.",
        TIME: "Complete before Friday afternoon.",
        EQUIPMENT:
          "Contractor needs gloves, rake, bags, saw for small limbs, and haul-off vehicle.",
      }),
      contact: { primary: "207-555-0164", preferredMethod: "SMS" },
    }),
    job({
      id: "job_shelf_install",
      title: "Install three garage storage shelves",
      summary: "Install three customer-provided wall shelves into garage studs.",
      category: "HANDYMAN",
      neighborhood: "Hampden Highlands",
      city: "Hampden",
      state: "ME",
      budgetMin: 100,
      budgetMax: 180,
      desiredWindow: "Next weekend",
      urgency: "ROUTINE",
      daysAgo: 9,
      scopeItems: scope("shelf", {
        SIZE: "Three wall-mounted garage shelves, each 48 inches wide.",
        ACTIVITY: "Locate studs and mount shelves level with supplied brackets.",
        LOCATION: "Attached garage with clear floor space and power outlet.",
        TIME: "Next weekend, morning preferred.",
        EQUIPMENT:
          "Customer has shelves and brackets. Contractor brings drill, level, stud finder, and fasteners if needed.",
      }),
      contact: { primary: "garage@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_subpanel_warning",
      title: "Install new electrical sub-panel",
      summary:
        "Customer wants a new sub-panel installed in the garage for future workshop equipment. This is regulated work.",
      category: "ELECTRICAL",
      neighborhood: "South Portland",
      city: "South Portland",
      state: "ME",
      budgetMin: 500,
      budgetMax: 1200,
      desiredWindow: "Quote this month",
      urgency: "ROUTINE",
      daysAgo: 10,
      scopeItems: scope("subpanel", {
        SIZE: "One garage sub-panel with multiple new circuits planned.",
        ACTIVITY: "Install sub-panel and connect to main service panel.",
        LOCATION: "Attached garage, main panel is in basement utility room.",
        TIME: "Customer wants quote this month.",
        EQUIPMENT:
          "Licensed electrician must determine materials, permit path, and load requirements.",
      }),
      contact: { primary: "owner@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_bulb_assist",
      title: "Change high ceiling light bulbs",
      summary: "Elderly customer needs four bulbs changed in a high-ceiling entryway.",
      category: "BASIC_LABOR",
      neighborhood: "Orono Village",
      city: "Orono",
      state: "ME",
      budgetMin: 60,
      budgetMax: 100,
      desiredWindow: "Flexible this week",
      urgency: "ROUTINE",
      daysAgo: 11,
      scopeItems: scope("bulb", {
        SIZE: "Four standard bulbs in an entryway fixture.",
        ACTIVITY: "Replace bulbs and verify switches work.",
        LOCATION: "Entryway ceiling is about 12 feet high. Floor is level.",
        TIME: "Flexible this week, daylight preferred.",
        EQUIPMENT: "Customer has bulbs. Contractor needs stable ladder.",
      }),
      contact: {
        primary: "207-555-0194",
        alternate: "family@example.com",
        preferredMethod: "PHONE",
      },
    }),
    job({
      id: "job_unknown_leak",
      title: "Figure out small leak under kitchen sink",
      summary: "Customer sees a damp cabinet base but is not sure where the leak starts.",
      category: "PLUMBING",
      neighborhood: "Brewer Center",
      city: "Brewer",
      state: "ME",
      budgetMin: 80,
      budgetMax: 180,
      desiredWindow: "Next 48 hours",
      urgency: "SOON",
      daysAgo: 12,
      scopeItems: scope(
        "leak",
        {
          SIZE: "Unknown leak under one kitchen sink cabinet.",
          ACTIVITY:
            "Inspect and fix simple connection if possible. Escalate if licensed work is needed.",
          LOCATION: "Kitchen sink cabinet with clear access but damp cabinet base.",
          TIME: "Next 48 hours.",
          EQUIPMENT:
            "Contractor should bring flashlight, towels, basic trap and supply-line tools.",
        },
        ["ACTIVITY", "LOCATION", "TIME", "EQUIPMENT"],
      ),
      contact: { primary: "brewer@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_bathroom_faucet",
      title: "Replace bathroom faucet with customer-provided fixture",
      summary:
        "Swap one vanity faucet and verify shutoffs hold. Escalate if valves fail or supply lines are corroded.",
      category: "PLUMBING",
      neighborhood: "Back Cove",
      city: "Portland",
      state: "ME",
      budgetMin: 140,
      budgetMax: 260,
      desiredWindow: "Within 7 days",
      urgency: "ROUTINE",
      daysAgo: 13,
      scopeItems: scope("faucet", {
        SIZE: "One bathroom vanity faucet, single sink.",
        ACTIVITY: "Remove existing faucet, install customer-provided faucet, test for leaks.",
        LOCATION: "Second-floor bathroom with clear cabinet access.",
        TIME: "Flexible within 7 days.",
        EQUIPMENT:
          "Customer has faucet. Contractor brings basin wrench, supply lines, towels, and plumber tape.",
      }),
      contact: { primary: "faucet@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_tree_limb",
      title: "Cut and move fallen tree limb from driveway edge",
      summary:
        "One fallen limb is blocking part of the driveway. No tree climbing and no overhead cutting requested.",
      category: "BASIC_LABOR",
      neighborhood: "North Deering",
      city: "Portland",
      state: "ME",
      budgetMin: 120,
      budgetMax: 240,
      desiredWindow: "This weekend",
      urgency: "SOON",
      daysAgo: 14,
      scopeItems: scope("limb", {
        SIZE: "One limb, roughly 10 inches at thickest point and 14 feet long.",
        ACTIVITY: "Cut into manageable sections and move to curb line.",
        LOCATION: "Driveway edge with vehicle access. No power lines nearby.",
        TIME: "This weekend during daylight.",
        EQUIPMENT:
          "Contractor needs saw, gloves, eye protection, and haul straps if removing debris.",
      }),
      contact: { primary: "limb@example.com", preferredMethod: "SMS" },
    }),
    job({
      id: "job_vaulted_fan",
      title: "Replace ceiling fan on vaulted ceiling",
      summary:
        "Existing fan on vaulted ceiling needs replacement. This combines heights and electrical risk and requires qualified review.",
      category: "ELECTRICAL",
      neighborhood: "Yarmouth",
      city: "Yarmouth",
      state: "ME",
      budgetMin: 250,
      budgetMax: 600,
      desiredWindow: "Quote requested",
      urgency: "ROUTINE",
      daysAgo: 15,
      scopeItems: scope("fan", {
        SIZE: "One fan on vaulted ceiling, estimated 16 feet high.",
        ACTIVITY: "Remove existing fan and install replacement fan.",
        LOCATION: "Living room, vaulted ceiling, furniture can be moved.",
        TIME: "Quote first, schedule later.",
        EQUIPMENT:
          "Licensed electrician or qualified contractor must determine access equipment and electrical safety.",
      }),
      contact: { primary: "fan@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_saturday_move",
      title: "Saturday four-bedroom local move support",
      summary:
        "Customer has a rental truck and needs two helpers for boxed household goods and furniture. Piano excluded.",
      category: "MOVING",
      neighborhood: "Westbrook",
      city: "Westbrook",
      state: "ME",
      budgetMin: 320,
      budgetMax: 520,
      desiredWindow: "Saturday 0900 to 1500",
      urgency: "SOON",
      daysAgo: 16,
      scopeItems: scope("move", {
        SIZE: "Four-bedroom home, boxed goods and standard furniture.",
        ACTIVITY: "Load truck at current home and unload at new home 4 miles away.",
        LOCATION: "Both homes have driveways. One set of stairs at destination.",
        TIME: "Saturday 0900 to 1500.",
        EQUIPMENT:
          "Customer has truck. Contractors need gloves, straps, and moving pads if available.",
      }),
      contact: { primary: "move@example.com", preferredMethod: "SMS" },
    }),
    job({
      id: "job_ceiling_stain",
      title: "Seal and repaint small ceiling water stain",
      summary:
        "Prior roof leak was repaired. Customer wants one stained ceiling area sealed and repainted after confirming it is dry.",
      category: "DRYWALL_PAINT",
      neighborhood: "Falmouth",
      city: "Falmouth",
      state: "ME",
      budgetMin: 160,
      budgetMax: 280,
      desiredWindow: "Within 2 weeks",
      urgency: "ROUTINE",
      daysAgo: 17,
      scopeItems: scope(
        "stain",
        {
          SIZE: "One ceiling stain roughly 18 by 24 inches.",
          ACTIVITY: "Confirm dry, apply stain-blocking primer, repaint local area.",
          LOCATION: "Second-floor bedroom ceiling, normal ladder height.",
          TIME: "Within 2 weeks.",
          EQUIPMENT:
            "Contractor needs moisture meter if available, primer, paint tools, drop cloth. Paint match still unconfirmed.",
        },
        ["SIZE", "ACTIVITY", "LOCATION", "TIME"],
      ),
      contact: { primary: "paint@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_wardrobe",
      title: "Assemble IKEA wardrobe and anchor to wall",
      summary:
        "One boxed wardrobe is already inside the room. Customer wants assembly, leveling, and wall anchoring.",
      category: "ASSEMBLY",
      neighborhood: "Old Port",
      city: "Portland",
      state: "ME",
      budgetMin: 140,
      budgetMax: 240,
      desiredWindow: "Weeknight after 1800",
      urgency: "ROUTINE",
      daysAgo: 18,
      scopeItems: scope("wardrobe", {
        SIZE: "One wardrobe, three boxes, customer-provided hardware.",
        ACTIVITY: "Assemble, level, and anchor to wall.",
        LOCATION: "Second-floor condo with elevator access.",
        TIME: "Weeknight after 1800.",
        EQUIPMENT: "Contractor brings drill, screwdriver bits, stud finder, and level.",
      }),
      contact: { primary: "wardrobe@example.com", preferredMethod: "SMS" },
    }),
    job({
      id: "job_pressure_wash",
      title: "Pressure wash short driveway and front steps",
      summary:
        "Concrete driveway and three front steps need light cleaning. Water spigot is accessible on the front wall.",
      category: "HANDYMAN",
      neighborhood: "Brewer Lake Road",
      city: "Brewer",
      state: "ME",
      budgetMin: 120,
      budgetMax: 220,
      desiredWindow: "Any dry day this week",
      urgency: "ROUTINE",
      daysAgo: 19,
      scopeItems: scope("wash", {
        SIZE: "One short single-car driveway and three concrete front steps.",
        ACTIVITY: "Pressure wash surface grime. No chemical treatment requested.",
        LOCATION: "Front of home, outdoor spigot available.",
        TIME: "Any dry day this week.",
        EQUIPMENT: "Contractor provides pressure washer, hose, and eye protection.",
      }),
      contact: { primary: "wash@example.com", preferredMethod: "EMAIL" },
    }),
    job({
      id: "job_flat_roof",
      title: "Inspect and seal small flat-roof leak area",
      summary:
        "Small flat-roof leak over porch needs inspection. This involves roof access and may require a licensed roofer.",
      category: "HANDYMAN",
      neighborhood: "East End",
      city: "Portland",
      state: "ME",
      budgetMin: 200,
      budgetMax: 450,
      desiredWindow: "Before next rain if qualified",
      urgency: "URGENT",
      daysAgo: 20,
      scopeItems: scope("roof", {
        SIZE: "One suspected leak area above covered porch.",
        ACTIVITY: "Inspect source and apply temporary seal only if safe and legally allowed.",
        LOCATION: "Flat roof access by exterior ladder. Fall hazard present.",
        TIME: "Before next rain if a qualified contractor is available.",
        EQUIPMENT:
          "Qualified contractor supplies ladder, fall-protection approach, and roofing sealant if appropriate.",
      }),
      contact: { primary: "roof@example.com", preferredMethod: "EMAIL" },
    }),
  ];
}

export function buildDemoContractors(): ContractorProfile[] {
  return [
    {
      id: "con_ready_handyman",
      businessName: "Ready Handyman Services",
      displayName: "Marcus Hale",
      serviceArea: "Bangor and Brewer",
      categories: ["HANDYMAN", "ASSEMBLY", "DRYWALL_PAINT", "PROPERTY_MAINTENANCE"],
      verificationState: "REFERENCE_READY",
      minimumJobValue: 75,
      availabilitySummary: "Weekday evenings and Saturday mornings.",
      trustStatement:
        "Former facilities lead. Prefers clear job details, no surprise add-ons, and direct contact.",
      contact: { primary: "marcus@example.com", preferredMethod: "EMAIL" },
      ratingSignal:
        "Self-reported references available on request. ClearScope has not checked them.",
    },
    {
      id: "con_gap_fill_builders",
      businessName: "Gap Fill Builders",
      displayName: "Anne Keller",
      serviceArea: "Greater Portland",
      categories: ["DRYWALL_PAINT", "HANDYMAN", "PROPERTY_MAINTENANCE"],
      verificationState: "LICENSE_CLAIMED",
      minimumJobValue: 175,
      availabilitySummary: "Short-notice weekday gaps only.",
      trustStatement:
        "General contractor who uses small well-defined jobs to fill schedule gaps between remodels.",
      contact: { primary: "anne@example.com", preferredMethod: "EMAIL" },
      ratingSignal:
        "Self-reported license claim. Verify directly with the state before work begins.",
    },
    {
      id: "con_retired_trade",
      businessName: "Selective Fix",
      displayName: "Tom Reyes",
      serviceArea: "Hampden, Bangor, Orono",
      categories: ["HANDYMAN", "ASSEMBLY", "BASIC_LABOR"],
      verificationState: "SELF_ATTESTED",
      minimumJobValue: 60,
      availabilitySummary: "One or two light jobs per week.",
      trustStatement: "Retired tradesman. Accepts only clear, low-risk work.",
      contact: { primary: "tom@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Profile not verified by ClearScope. Customer should verify directly.",
    },
    {
      id: "con_yard_route",
      businessName: "North Route Yard Help",
      displayName: "Leah Grant",
      serviceArea: "Cape Elizabeth and South Portland",
      categories: ["LANDSCAPE", "BASIC_LABOR", "MOVING"],
      verificationState: "REFERENCE_READY",
      minimumJobValue: 120,
      availabilitySummary: "Route-based service on Tuesday, Thursday, and Saturday.",
      trustStatement: "Optimizes small jobs by route density to keep cost reasonable.",
      contact: { primary: "207-555-0212", preferredMethod: "SMS" },
      ratingSignal:
        "Self-reported neighborhood references available. ClearScope has not verified them.",
    },
    {
      id: "con_plumb_basic",
      businessName: "Shutoff Ready Repairs",
      displayName: "Nora Singh",
      serviceArea: "Bangor metro",
      categories: ["PLUMBING", "HANDYMAN"],
      verificationState: "LICENSE_CLAIMED",
      minimumJobValue: 150,
      availabilitySummary: "Emergency small plumbing triage when available.",
      trustStatement:
        "Handles basic leak triage and directs licensed work when the job exceeds limits.",
      contact: { primary: "nora@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "License claim is self-reported and must be verified before work.",
    },
    {
      id: "con_student_helper",
      businessName: "Weekend Helper",
      displayName: "Cal Brooks",
      serviceArea: "Orono and Bangor",
      categories: ["ASSEMBLY", "BASIC_LABOR", "MOVING"],
      verificationState: "UNVERIFIED",
      minimumJobValue: 50,
      availabilitySummary: "Weekends and evenings around school schedule.",
      trustStatement:
        "Accepts basic labor only. No electrical, plumbing, structural, or permit work.",
      contact: { primary: "cal@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "New profile. Direct verification required.",
    },
    {
      id: "con_veteran_ops",
      businessName: "Steady Line Home Services",
      displayName: "R. Morris",
      serviceArea: "Portland and Deering",
      categories: ["HANDYMAN", "PROPERTY_MAINTENANCE", "ASSEMBLY"],
      verificationState: "REFERENCE_READY",
      minimumJobValue: 95,
      availabilitySummary: "After-hours service with confirmed job details only.",
      trustStatement:
        "Veteran-owned small business focused on punctuality, clean work areas, and clear communication.",
      contact: { primary: "steadyline@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Self-reported repeat customers available as references.",
    },
    {
      id: "con_paint_patch",
      businessName: "Patch Ready Painting",
      displayName: "June Miller",
      serviceArea: "Portland peninsula",
      categories: ["DRYWALL_PAINT"],
      verificationState: "SELF_ATTESTED",
      minimumJobValue: 125,
      availabilitySummary: "Small patch and paint-ready jobs on weekday mornings.",
      trustStatement: "Does small repairs that larger crews usually decline.",
      contact: { primary: "june@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Profile not verified by ClearScope. Verify fit before scheduling.",
    },
    {
      id: "con_small_team",
      businessName: "Two-Man Route Crew",
      displayName: "Dane and Eli",
      serviceArea: "Westbrook, Falmouth, Portland",
      categories: ["MOVING", "BASIC_LABOR", "LANDSCAPE"],
      verificationState: "SELF_ATTESTED",
      minimumJobValue: 180,
      availabilitySummary: "Two-person crew available Saturdays and some Fridays.",
      trustStatement:
        "Best fit for appliance moves, boxed local moves, and yard haul-off where the job is clear.",
      contact: { primary: "routecrew@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Small-team profile. ClearScope has not checked identities or insurance.",
    },
    {
      id: "con_assembly_focus",
      businessName: "Flat Pack Finish",
      displayName: "Mia Ortiz",
      serviceArea: "Portland condos and apartments",
      categories: ["ASSEMBLY", "HANDYMAN"],
      verificationState: "SELF_ATTESTED",
      minimumJobValue: 85,
      availabilitySummary: "Evening assembly blocks, 2 hour minimum.",
      trustStatement: "Specializes in furniture assembly, anchoring, and clean room turnover.",
      contact: { primary: "mia@example.com", preferredMethod: "SMS" },
      ratingSignal:
        "Profile not verified by ClearScope. Ask for photos of recent work before scheduling.",
    },
    {
      id: "con_pressure_wash",
      businessName: "Clean Edge Exterior",
      displayName: "Sam Nguyen",
      serviceArea: "Brewer and Bangor suburbs",
      categories: ["HANDYMAN", "LANDSCAPE", "PROPERTY_MAINTENANCE"],
      verificationState: "REFERENCE_READY",
      minimumJobValue: 125,
      availabilitySummary: "Dry-weather exterior work, weekday afternoons.",
      trustStatement:
        "Works from short exterior briefs and avoids roof or ladder work above first story.",
      contact: { primary: "cleanedge@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "References self-reported. Verify before accepting.",
    },
    {
      id: "con_electrical_only",
      businessName: "Panel Safe Electrical",
      displayName: "Owen Price",
      serviceArea: "Southern Maine",
      categories: ["ELECTRICAL"],
      verificationState: "LICENSE_CLAIMED",
      minimumJobValue: 250,
      availabilitySummary: "Quotes regulated electrical work only after job review.",
      trustStatement: "License claim is provided by the contractor and must be checked directly.",
      contact: { primary: "panel@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "License claim is self-reported. ClearScope does not verify licenses.",
    },
    {
      id: "con_senior_helper",
      businessName: "Light Lift Help",
      displayName: "Bea Wallace",
      serviceArea: "Orono, Old Town, Bangor",
      categories: ["BASIC_LABOR", "ASSEMBLY"],
      verificationState: "SELF_ATTESTED",
      minimumJobValue: 55,
      availabilitySummary: "Short daytime help for simple tasks.",
      trustStatement: "Focuses on low-risk help such as bulbs, small boxes, and simple assembly.",
      contact: { primary: "bea@example.com", preferredMethod: "PHONE" },
      ratingSignal:
        "Profile not verified by ClearScope. Customers should use direct safety checks.",
    },
    {
      id: "con_property_turnover",
      businessName: "Unit Turn Assist",
      displayName: "Pat Collins",
      serviceArea: "Bangor rental units",
      categories: ["PROPERTY_MAINTENANCE", "DRYWALL_PAINT", "HANDYMAN"],
      verificationState: "REFERENCE_READY",
      minimumJobValue: 110,
      availabilitySummary: "Recurring property turnover windows by appointment.",
      trustStatement:
        "Best fit for small recurring maintenance jobs with photos and access instructions.",
      contact: { primary: "unitturn@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Property references self-reported. Verify before work.",
    },
    {
      id: "con_roof_referral",
      businessName: "Roof Review Referral",
      displayName: "Evan Reed",
      serviceArea: "Portland metro",
      categories: ["HANDYMAN", "PROPERTY_MAINTENANCE"],
      verificationState: "UNVERIFIED",
      minimumJobValue: 175,
      availabilitySummary: "Only accepts ground-level exterior inspection and referral jobs.",
      trustStatement: "Will decline roof-access work if fall protection or licensing is unclear.",
      contact: { primary: "evan@example.com", preferredMethod: "EMAIL" },
      ratingSignal: "Unverified profile. Use only with direct credential checks.",
    },
  ];
}
